import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function ConfirmationPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { booking } = state || {};

  if (!booking) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-500 text-lg">No booking details found.</p>
        <button onClick={() => navigate('/')} className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg">
          Go Home
        </button>
      </div>
    );
  }

  const { flight, bookingId, pnr, status, totalAmount, passengers, contactEmail, travelClass } = booking;
  const classLabel = { economy: 'Economy', business: 'Business', firstClass: 'First Class' }[travelClass];

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <SEO
        title="Booking Confirmed"
        description="Your flight booking is confirmed. Check your email for your e-ticket and PNR details."
        noindex={true}
      />
      {/* Success Banner */}
      <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center mb-8">
        <div className="text-6xl mb-3">🎉</div>
        <h1 className="text-3xl font-bold text-green-700 mb-2">Booking Confirmed!</h1>
        <p className="text-green-600">Your flight has been successfully booked. A confirmation will be sent to <strong>{contactEmail}</strong>.</p>
      </div>

      {/* Booking Info */}
      <div className="bg-white rounded-xl shadow p-6 mb-6 border border-gray-100">
        <h2 className="font-bold text-gray-800 text-lg mb-4">Booking Details</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-center">
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Booking ID</p>
            <p className="font-bold text-blue-700 text-sm">{bookingId}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">PNR</p>
            <p className="font-bold text-blue-700 text-lg tracking-widest">{pnr}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Status</p>
            <p className="font-bold text-green-600">{status}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Total Paid</p>
            <p className="font-bold text-gray-800">${totalAmount?.toLocaleString('en-US')}</p>
          </div>
        </div>
      </div>

      {/* Flight Details */}
      <div className="bg-white rounded-xl shadow p-6 mb-6 border border-gray-100">
        <h2 className="font-bold text-gray-800 text-lg mb-4">✈️ Flight Details</h2>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <p className="text-xl font-bold text-gray-800">
              {flight.originCity} → {flight.destinationCity}
            </p>
            <p className="text-gray-600">{flight.airline} • {flight.flightNumber}</p>
            <p className="text-gray-600">{flight.date} • {flight.departureTime} – {flight.arrivalTime}</p>
            <p className="text-gray-500 text-sm">Class: {classLabel} • Duration: {flight.duration}</p>
          </div>
          <div className="text-4xl">✈️</div>
        </div>
      </div>

      {/* Passengers */}
      <div className="bg-white rounded-xl shadow p-6 mb-8 border border-gray-100">
        <h2 className="font-bold text-gray-800 text-lg mb-4">👥 Passengers</h2>
        <div className="space-y-2">
          {passengers?.map((pax, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
              <span className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">{i + 1}</span>
              <span className="font-medium text-gray-800">{pax.firstName} {pax.lastName}</span>
              <span className="text-gray-500 text-sm capitalize">{pax.gender}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Link
          to="/"
          className="flex-1 text-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-colors"
        >
          Book Another Flight
        </Link>
        <Link
          to="/my-bookings"
          className="flex-1 text-center border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold py-3 rounded-xl transition-colors"
        >
          View My Bookings
        </Link>
      </div>
    </div>
  );
}
